import React, { createContext, useContext, useState, useEffect } from 'react';
import { Class, Student, ScenarioAssignment } from '../types';

interface ClassContextType {
  currentClass: Class | null;
  setCurrentClass: (classData: Class) => void;
  addStudent: (firstName: string, lastName: string) => void;
  removeStudent: (studentId: string) => void;
  clearClass: () => void;
  assignScenarios: (assignments: ScenarioAssignment[]) => void;
  clearAssignments: () => void;
}

const ClassContext = createContext<ClassContextType | undefined>(undefined);

export function ClassProvider({ children }: { children: React.ReactNode }) {
  const [currentClass, setCurrentClass] = useState<Class | null>(() => {
    const saved = localStorage.getItem('currentClass');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    if (currentClass) {
      localStorage.setItem('currentClass', JSON.stringify(currentClass));
    } else {
      localStorage.removeItem('currentClass');
    }
  }, [currentClass]);

  const addStudent = (firstName: string, lastName: string) => {
    if (!currentClass) return;

    const newStudent: Student = {
      id: Date.now().toString(),
      firstName,
      lastName,
      evaluations: {}
    };

    setCurrentClass({
      ...currentClass,
      students: [...currentClass.students, newStudent]
    });
  };

  const removeStudent = (studentId: string) => {
    if (!currentClass) return;

    setCurrentClass({
      ...currentClass,
      students: currentClass.students.filter(student => student.id !== studentId)
    });
  };

  const clearClass = () => {
    setCurrentClass(null);
  };

  const assignScenarios = (assignments: ScenarioAssignment[]) => {
    if (!currentClass) return;

    setCurrentClass({
      ...currentClass,
      students: currentClass.students.map(student => {
        const assignment = assignments.find(a => a.studentId === student.id);
        return {
          ...student,
          assignedScenario: assignment?.scenarioId
        };
      })
    });
  };

  const clearAssignments = () => {
    if (!currentClass) return;

    setCurrentClass({
      ...currentClass,
      students: currentClass.students.map(student => ({
        ...student,
        assignedScenario: undefined
      }))
    });
  };

  return (
    <ClassContext.Provider 
      value={{ 
        currentClass, 
        setCurrentClass, 
        addStudent, 
        removeStudent,
        clearClass,
        assignScenarios,
        clearAssignments
      }}
    >
      {children}
    </ClassContext.Provider>
  );
}

export function useClass() {
  const context = useContext(ClassContext);
  if (context === undefined) {
    throw new Error('useClass must be used within a ClassProvider');
  }
  return context;
}